// Find-in-textarea search bar: navigates matches with up/down, highlights via setSelectionRange.
import { useEffect, useMemo, useRef, useState } from "react";
import { CaretDown, CaretUp, X, MagnifyingGlass } from "@phosphor-icons/react";

export default function TextSearch({ value, textareaRef, testid = "text-search" }) {
  const [q, setQ] = useState("");
  const [idx, setIdx] = useState(0);
  const inputRef = useRef(null);

  const matches = useMemo(() => {
    if (!q.trim() || !value) return [];
    const out = [];
    const lc = value.toLowerCase();
    const ql = q.toLowerCase();
    let pos = 0;
    while (pos < lc.length) {
      const m = lc.indexOf(ql, pos);
      if (m === -1) break;
      out.push(m);
      pos = m + Math.max(ql.length, 1);
    }
    return out;
  }, [q, value]);

  useEffect(() => { setIdx(0); }, [q]);

  const goTo = (i) => {
    if (!matches.length || !textareaRef.current) return;
    const at = matches[i];
    const ta = textareaRef.current;
    // Force a fresh focus + selection — Safari/Chrome both honor scroll-on-focus
    // when the selection is set DURING the focus event sequence.
    requestAnimationFrame(() => {
      try {
        ta.focus({ preventScroll: true });
        ta.setSelectionRange(at, at + q.length);
        // Mirror-div trick: measure the EXACT pixel position of `at` within the
        // textarea's wrapped layout, then scroll so the match sits ~30% from the top.
        const cs = getComputedStyle(ta);
        const mirror = document.createElement("div");
        mirror.style.position = "absolute";
        mirror.style.visibility = "hidden";
        mirror.style.whiteSpace = "pre-wrap";
        mirror.style.wordWrap = "break-word";
        mirror.style.overflowWrap = "break-word";
        // Copy width + box model + typography
        const propsToCopy = [
          "boxSizing", "width", "paddingTop", "paddingRight", "paddingBottom", "paddingLeft",
          "borderTopWidth", "borderRightWidth", "borderBottomWidth", "borderLeftWidth",
          "fontFamily", "fontSize", "fontWeight", "fontStyle", "lineHeight",
          "letterSpacing", "tabSize", "textTransform", "textIndent", "wordSpacing",
        ];
        for (const p of propsToCopy) mirror.style[p] = cs[p];
        mirror.style.width = ta.clientWidth + "px";
        mirror.style.height = "auto";
        mirror.textContent = ta.value.slice(0, at);
        const marker = document.createElement("span");
        marker.textContent = ta.value.slice(at, at + q.length) || "\u200b";
        mirror.appendChild(marker);
        document.body.appendChild(mirror);
        const offsetTop = marker.offsetTop;
        document.body.removeChild(mirror);
        const target = Math.max(0, offsetTop - ta.clientHeight * 0.3);
        ta.scrollTop = target;
      } catch (_) {
        // Fallback: proportional scroll
        try {
          const ratio = at / Math.max(1, ta.value.length);
          ta.scrollTop = Math.max(0, ratio * (ta.scrollHeight - ta.clientHeight));
        } catch (_e) {}
      }
    });
  };

  const next = () => { if (!matches.length) return; const n = (idx + 1) % matches.length; setIdx(n); goTo(n); };
  const prev = () => { if (!matches.length) return; const n = (idx - 1 + matches.length) % matches.length; setIdx(n); goTo(n); };

  return (
    <div className="flex items-center gap-1 text-xs" data-testid={testid}>
      <div className="relative flex-1 min-w-[110px]">
        <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#6B6A68] pointer-events-none" />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); e.shiftKey ? prev() : next(); }
            if (e.key === "Escape") { setQ(""); }
          }}
          placeholder="Find…"
          data-testid={`${testid}-input`}
          className="w-full bg-[#FDFCF8] border hairline rounded-md pl-7 pr-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
        />
      </div>
      {q && (
        <span className="tabular-nums text-[#6B6A68] px-1" data-testid={`${testid}-count`}>
          {matches.length === 0 ? "0" : `${idx + 1}/${matches.length}`}
        </span>
      )}
      <button type="button" onClick={prev} disabled={!matches.length} data-testid={`${testid}-prev`}
        className="p-1 rounded-md text-[#6B6A68] hover:text-[#4A5D4E] disabled:opacity-30 hover:bg-[#F0F1EE]" title="Previous (Shift+Enter)">
        <CaretUp size={12} />
      </button>
      <button type="button" onClick={next} disabled={!matches.length} data-testid={`${testid}-next`}
        className="p-1 rounded-md text-[#6B6A68] hover:text-[#4A5D4E] disabled:opacity-30 hover:bg-[#F0F1EE]" title="Next (Enter)">
        <CaretDown size={12} />
      </button>
      {q && (
        <button type="button" onClick={() => setQ("")} data-testid={`${testid}-clear`}
          className="p-1 rounded-md text-[#6B6A68] hover:text-[#C16E5A] hover:bg-[#F0F1EE]" title="Clear">
          <X size={12} />
        </button>
      )}
    </div>
  );
}
